<template>
  <div>
    <div class="card">
      <div class="card-body">
        <h4 class="card-title mb-3">{{ title }}</h4>

        <div class="row mb-3">
          <div class="col-md-3 form-group">
            <label class="font-size-12 text-muted mb-1">Data inicial</label>
            <input type="date" class="form-control form-control-sm" v-model="filters.date_from">
          </div>
          <div class="col-md-3 form-group">
            <label class="font-size-12 text-muted mb-1">Data final</label>
            <input type="date" class="form-control form-control-sm" v-model="filters.date_to">
          </div>
          <div class="col-md-3 form-group">
            <label class="font-size-12 text-muted mb-1">Categoria</label>
            <select class="custom-select custom-select-sm" v-model="filters.category">
              <option value="">Todas</option>
              <option v-for="c in categories" :key="c.slug" :value="c.slug">{{ c.name }}</option>
            </select>
          </div>
          <div class="col-md-3 form-group">
            <label class="font-size-12 text-muted mb-1">Forma de pagamento</label>
            <select class="custom-select custom-select-sm" v-model="filters.payment_method">
              <option value="">Todas</option>
              <option v-for="p in paymentMethods" :key="p.slug" :value="p.slug">{{ p.name }}</option>
            </select>
          </div>
          <div class="col-md-6 form-group">
            <label class="font-size-12 text-muted mb-1">Busca</label>
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Participante, descrição, pagador, doc..."
              v-model="filters.search"
              @keyup.enter="applyFilters()">
          </div>
          <div class="col-md-6 d-flex align-items-end form-group" style="gap:8px">
            <button class="btn btn-sm btn-primary" @click="applyFilters()">
              <i class="mdi mdi-magnify"></i> Aplicar
            </button>
            <button class="btn btn-sm btn-outline-secondary" @click="clearFilters()">
              Limpar
            </button>
            <button class="btn btn-sm btn-success ml-auto" @click="exportXlsx()" :disabled="exporting">
              <i class="mdi mdi-file-export-outline mr-1"></i> {{ exporting ? 'Exportando...' : 'Exportar Excel' }}
            </button>
          </div>
        </div>

        <div class="d-flex justify-content-between mb-2">
          <div class="text-muted font-size-12">
            <strong>{{ totals.count }}</strong> lançamento(s)
          </div>
          <div class="font-size-13">
            Total ({{ baseCurrency }}): <strong>{{ formatCurrency(totals.amount_base) }}</strong>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-centered table-hover w-100">
            <thead>
              <tr>
                <th>Data</th>
                <th>Grupo</th>
                <th>Participante</th>
                <th>Acompanhante de</th>
                <th>Pagador</th>
                <th>Descrição</th>
                <th>Forma pgto</th>
                <th class="text-right">Valor ({{ baseCurrency }})</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!records.length">
                <td colspan="8" class="text-center text-muted py-3">Nenhum lançamento encontrado.</td>
              </tr>
              <tr v-for="r in records" :key="r.id">
                <td style="white-space:nowrap">{{ formatDate(r.transaction_date) }}</td>
                <td>{{ r.participant_group || '—' }}</td>
                <td>
                  <div class="font-size-13">{{ fullName(r.participant_name, r.participant_surname) }}</div>
                  <small class="text-muted">{{ r.participant_email }}</small>
                </td>
                <td>
                  <span v-if="r.is_companion">
                    {{ fullName(r.main_participant_name, r.main_participant_surname) }}
                    <small v-if="r.main_participant_group" class="text-muted d-block">{{ r.main_participant_group }}</small>
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>{{ r.payer_name || '—' }}</td>
                <td>{{ r.description }}</td>
                <td>{{ r.payment_method_label || r.payment_method }}</td>
                <td class="text-right">{{ formatCurrency(r.amount_base) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="row">
          <div class="col">
            <div class="dataTables_paginate paging_simple_numbers float-right">
              <ul class="pagination pagination-rounded mb-0">
                <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage" @input="loadReport()"></b-pagination>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import serviceReport from '../services'
import { getFinancialSettings } from '@/pages/settings/financial-control/services'
import { errorMessage } from '@/components/alertsMessages'

export default {
  name: 'report-participantsFinancials',
  data () {
    return {
      title: 'Relatório de Lançamentos Financeiros',
      filters: {
        date_from: '',
        date_to: '',
        category: '',
        payment_method: '',
        search: ''
      },
      records: [],
      perPage: 50,
      currentPage: 1,
      rows: 0,
      totals: { count: 0, amount_base: 0 },
      baseCurrency: 'BRL',
      categories: [],
      paymentMethods: [],
      exporting: false
    }
  },
  mounted () {
    this.loadConfig().then(() => this.loadReport())
  },
  methods: {
    async loadConfig () {
      try {
        const res = await getFinancialSettings()
        const data = res.data?.data || {}
        this.baseCurrency = data.base_currency || 'BRL'
        this.categories = (data.categories || []).filter(c => c.active)
        this.paymentMethods = (data.payment_methods || []).filter(p => p.active)
      } catch (e) {
        // silent fallback
      }
    },
    async loadReport () {
      const loader = this.$loading.show()
      try {
        const params = { ...this.filters, page: this.currentPage, limit: this.perPage }
        const res = await serviceReport.listParticipantsFinancials(params)
        this.records = res.data || []
        this.rows = res.meta?.pagination?.total || 0
        this.totals = res.meta?.totals || { count: 0, amount_base: 0 }
        this.baseCurrency = res.meta?.base_currency || this.baseCurrency
      } catch (e) {
        const msg = e?.response?.data?.message || 'Falha ao carregar relatório.'
        errorMessage(msg, 3000)
      } finally {
        loader.hide()
      }
    },
    applyFilters () {
      this.currentPage = 1
      this.loadReport()
    },
    clearFilters () {
      this.filters = { date_from: '', date_to: '', category: '', payment_method: '', search: '' }
      this.applyFilters()
    },
    async exportXlsx () {
      this.exporting = true
      try {
        const data = await serviceReport.exportParticipantsFinancials(this.filters)
        const url = window.URL.createObjectURL(new Blob([data]))
        const a = document.createElement('a')
        a.href = url
        a.download = 'lancamentos-financeiros.xlsx'
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        errorMessage('Falha ao exportar.', 3000)
      } finally {
        this.exporting = false
      }
    },
    formatDate (date) {
      if (!date) return ''
      return this.$moment(date).format('DD/MM/YYYY')
    },
    fullName (name, surname) {
      return [name, surname].filter(Boolean).join(' ') || '—'
    },
    formatCurrency (val) {
      const num = Number(val) || 0
      return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }
}
</script>
